"use client"
import { graphql, useMutation, useQuery } from "@opensea/graphql"
import { TimelineSkeleton } from "@opensea/ui-kit"
import type { EVMAddress } from "@opensea/utils"
import { useCallback } from "react"
import {
  ActionTimeline,
  ActionTimelineError,
} from "../../../../../components/ActionTimeline"
import { ActionTimelineFragment } from "../../../../../components/ActionTimeline/ActionTimeline.fragment"
import { useAddress } from "../../../../../polychain/hooks"
import type { SchedulableResponse } from "../../../../../polychain/hooks/useScheduler"
import { useTrackError } from "../../../../../providers/TrackErrorProvider"
import type { FlowType } from "../../../../flow-state"
import { useActionErrors } from "../../../../hooks/useErrors"
import { useSetBlockchainActionErrors } from "../../../../hooks/useSetBlockchainActionErrors"
import { useSetBlockchainActionErrorsFragment } from "../../../../hooks/useSetBlockchainActionErrors.fragment"
import { readOrderCreationError } from "../../../../utils/readOrderCreationError"
import { readOrderCreationErrorFragment } from "../../../../utils/readOrderCreationError.fragment"
import { seaportOrder } from "../../../../utils/seaportOrder"
import { seaportOrderFragment } from "../../../../utils/seaportOrder.fragment"
import type { Offer } from "../../types"

type CreateOfferTimelineProps = {
  offers: Offer[]
  onSuccess?: () => void
  onError?: (error: Error) => void
  onBack?: () => void
}

export const CreateOfferTimeline = ({
  offers,
  onError,
  onSuccess,
  onBack,
}: CreateOfferTimelineProps) => {
  const address = useAddress()
  const [{ data, error }, refetch] = useQuery({
    query: CreateOfferTimelineQuery,
    variables: {
      address: address as EVMAddress,
      offers: offers.map(offer => ({
        item: {
          tokenId: offer.item.tokenId,
          contractAddress: offer.item.contractAddress,
          chain: offer.item.chain.identifier,
        },
        quantity: offer.quantity,
        pricePerItem: {
          unit: offer.pricePerItem.unit.toString(),
          contractAddress: offer.pricePerItem.contractAddress,
        },
        startTime: offer.startTime.toISOString(),
        endTime: offer.endTime.toISOString(),
      })),
    },
    pause: !address,
  })

  const trackError = useTrackError()

  const [_, mutate] = useMutation(CreateOfferTimelineMutation)

  const actions = data?.createOffersActions.actions

  const onDone = useCallback(
    async (response: SchedulableResponse) => {
      if (response.action !== "SIGNATURE") {
        throw new Error("No signature response found from scheduled actions")
      }

      const actionWithOrders = actions?.find(
        action => action.__typename === "CreateOffersAction",
      )

      if (actionWithOrders?.__typename === "CreateOffersAction") {
        const orders = actionWithOrders.orders.map(order => seaportOrder(order))
        const chain = actionWithOrders.items[0].chain.identifier

        const mutation = await mutate({
          orders,
          chain,
          signature: response.signature,
        })

        const dataError = readOrderCreationError(mutation.data?.createOffers)

        if (mutation.error) {
          trackError(mutation.error, {
            extra: { offers },
            tags: {
              flow: "create-offer" satisfies FlowType,
              address: address || "",
            },
          })
          onError?.(new Error(mutation.error.message))
        } else if (dataError) {
          onError?.(new Error(dataError.message))
        } else {
          onSuccess?.()
        }
      }
    },
    [actions, address, mutate, offers, onError, onSuccess, trackError],
  )

  useSetBlockchainActionErrors(data?.createOffersActions.errors)

  const actionError = useActionErrors()

  if (!actions) {
    return <TimelineSkeleton />
  }

  if (error) {
    return (
      <ActionTimelineError
        message="Something went wrong"
        onBack={onBack}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    )
  }

  if (actionError) {
    return (
      <ActionTimelineError
        message={actionError}
        onBack={onBack}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    )
  }

  return (
    <ActionTimeline
      actions={actions}
      onAllActionsCompleted={onDone}
      onBack={onBack}
    />
  )
}

const CreateOfferTimelineQuery = graphql(
  `
    query CreateOfferTimelineQuery(
      $offers: [ItemOfferInput!]!
      $address: Address!
    ) {
      createOffersActions(
        offers: $offers
        address: $address
      ) {
        actions {
          __typename
          ... on CreateOffersAction {
            items {
              chain {
                identifier
              }
            }
            orders {
              ...seaportOrder
            }
          }
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `,
  [
    ActionTimelineFragment,
    seaportOrderFragment,
    useSetBlockchainActionErrorsFragment,
  ],
)

const CreateOfferTimelineMutation = graphql(
  `
    mutation CreateOfferTimelineMutation(
      $chain: ChainIdentifier!
      $orders: [SeaportOrderComponentsInput!]!
      $signature: String!
    ) {
      createOffers(chain: $chain, orders: $orders, signature: $signature) {
        ...readOrderCreationError
      }
    }
  `,
  [readOrderCreationErrorFragment],
)
